<script setup lang="ts">
import { ref, nextTick, watch } from 'vue'
import { IconSparkles, IconTrash, IconX, IconSend } from '@tabler/icons-vue'
import type { ChatMessage } from '../composables/useChatAssistant'

const props = defineProps<{
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
}>()

const inputText = defineModel<string>('inputText', { required: true })

const emit = defineEmits<{
  send: []
  clear: []
  close: []
}>()

const messagesContainer = ref<HTMLElement | null>(null)

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

watch(() => props.messages.length, scrollToBottom)
watch(() => props.isLoading, scrollToBottom)

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    emit('send')
  }
}

function formatTime(date: Date) {
  return new Date(date).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })
}
</script>

<template>
  <div class="fixed bottom-24 right-6 w-[380px] max-h-[560px] bg-base-400 border border-white/[0.06] rounded-2xl shadow-[0_0_60px_rgba(0,0,0,0.5)] z-40 flex flex-col max-sm:bottom-0 max-sm:right-0 max-sm:left-0 max-sm:w-full max-sm:h-full max-sm:max-h-full max-sm:rounded-none">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
      <div class="flex items-center gap-2">
        <IconSparkles class="w-5 h-5 text-amber-400" />
        <span class="text-sm font-semibold text-gray-200">AI Asistent</span>
        <span class="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-400/10 text-amber-400 font-medium">AI</span>
      </div>
      <div class="flex items-center gap-1">
        <button
          v-if="messages.length > 0"
          class="rail-btn !w-7 !h-7"
          title="Vymazat historii"
          @click="$emit('clear')"
        >
          <IconTrash class="w-3.5 h-3.5" />
        </button>
        <button
          class="rail-btn !w-7 !h-7 sm:hidden"
          title="Zavřít"
          @click="$emit('close')"
        >
          <IconX class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Messages -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
      <!-- Empty state -->
      <div v-if="messages.length === 0 && !isLoading" class="flex flex-col items-center justify-center h-full text-center py-12">
        <div class="w-12 h-12 rounded-full bg-amber-400/10 flex items-center justify-center mb-3">
          <IconSparkles class="w-6 h-6 text-amber-400" />
        </div>
        <p class="text-sm text-gray-400">Zeptejte se na cokoliv</p>
        <p class="text-xs text-gray-500 mt-1">AI asistent vám pomůže s administrací</p>
      </div>

      <!-- Message list -->
      <template v-for="message in messages" :key="message.id">
        <!-- User message -->
        <div v-if="message.role === 'user'" class="flex justify-end">
          <div class="max-w-[85%] bg-amber-400/10 rounded-xl rounded-br-sm px-3 py-2">
            <p class="text-sm text-gray-200 whitespace-pre-wrap">{{ message.content }}</p>
            <p class="text-[10px] text-gray-500 mt-1 text-right">{{ formatTime(message.timestamp) }}</p>
          </div>
        </div>
        <!-- Assistant message -->
        <div v-else class="flex justify-start">
          <div class="max-w-[85%] bg-base-300 border border-white/[0.06] rounded-xl rounded-bl-sm px-3 py-2">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-if="message.htmlContent" class="text-sm text-gray-300 prose prose-sm prose-invert max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>pre]:my-2" v-html="message.htmlContent" />
            <p v-else class="text-sm text-gray-300 whitespace-pre-wrap">{{ message.content }}</p>
            <p class="text-[10px] text-gray-500 mt-1">{{ formatTime(message.timestamp) }}</p>
          </div>
        </div>
      </template>

      <!-- Typing indicator -->
      <div v-if="isLoading" class="flex justify-start">
        <div class="bg-base-300 border border-white/[0.06] rounded-xl rounded-bl-sm px-4 py-3">
          <div class="flex gap-1">
            <span class="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:0ms]" />
            <span class="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:150ms]" />
            <span class="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:300ms]" />
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="px-4 pb-2">
      <p class="text-xs text-red-400">{{ error }}</p>
    </div>

    <!-- Input -->
    <div class="p-3 border-t border-white/[0.06]">
      <div class="flex items-end gap-2">
        <textarea
          v-model="inputText"
          rows="1"
          class="flex-1 resize-none bg-base-300 border border-white/[0.06] rounded-full px-4 py-2.5 text-sm text-gray-200 placeholder:text-gray-500 outline-none focus:border-amber-400/30 focus:shadow-[0_0_24px_rgba(226,179,64,0.08)] transition-all max-h-[120px]"
          placeholder="Napište zprávu..."
          @keydown="handleKeydown"
        />
        <button
          class="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 flex items-center justify-center flex-shrink-0 hover:bg-amber-500/30 hover:border-amber-500/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="!inputText.trim() || isLoading"
          @click="$emit('send')"
        >
          <IconSend class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>
